import CommonService from "../Common";
import { AxiosRequestConfig } from "axios";

interface Project {
  pathPrefix?: string;
  commonService: CommonService;
}

// Type definitions matching Joi validation schema
interface BasicCompanyInfo {
  companyName: string;
  industrySector: string;
  companySize: number;
  annualRevenueRange: string;
  geographicLocations: string[];
  yearsInBusiness: number;
  currentGrowthStage:
    | "All"
    | "Pre-seed"
    | "Seed"
    | "Series A"
    | "Series B+"
    | "Mature";
  websiteUrl: string;
}

interface ProductServiceDetails {
  coreDescription: string;
  currentProductStage:
    | "All"
    | "Idea/Concept"
    | "MVP"
    | "Beta"
    | "Launched"
    | "Scaling";
  primaryValueProposition: string;
  keyFeaturesBenefits: string[];
  currentPricingModel: Record<string, string>;
  deliveryMethod:
    | "SaaS"
    | "Physical Product"
    | "Digital Product"
    | "Service"
    | "Hybrid";
  implementationRequirements: string;
  technicalRequirements: string;
}

interface CurrentMarketPosition {
  targetMarketDescription: string;
  existingCustomerBaseSize: number;
  topCustomerIndustries: string[];
  averageDealSize: string;
  salesCycleLength: string;
  customerAcquisitionChannels: string[];
  mainCompetitors: string[];
  marketDifferentiators: string;
}

interface CustomerSuccessMetrics {
  retentionRate: string;
  averageCustomerLifetimeValue: string;
  mostCommonUseCases: string[];
  kpis: string[];
  customerSuccessStories: string;
  commonPainPointsSolved: string[];
  typicalRoi: string;
  customerSatisfactionMetrics: string;
}

interface BusinessGoals {
  shortTermRevenueTargets: string;
  longTermGrowthObjectives: string;
  targetMarketExpansion: string;
  desiredCustomerProfileChanges: string;
  priorityIndustries: string[];
  geographicExpansionGoals: string;
  productRoadmapHighlights: string;
  scalabilityRequirements: string;
}

interface BudgetResourceAllocation {
  customerAcquisitionCostTargets: string;
  marketingBudget: string;
  salesDevelopmentResources: string;
  customerSuccessResources: string;
  technicalSupportResources: string;
  trainingResources: string;
  integrationSupportResources: string;
  growthInvestmentPlans: string;
}

interface ProjectData {
  userId: string;
  basicCompanyInfo: BasicCompanyInfo;
  productServiceDetails: ProductServiceDetails;
  currentMarketPosition: CurrentMarketPosition;
  customerSuccessMetrics: CustomerSuccessMetrics;
  businessGoals: BusinessGoals;
  budgetResourceAllocation: BudgetResourceAllocation;
}

interface GetProjectsParams {
  userId: string;
  limit?: number;
  page?: number;
  sortBy?:
    | "createdAt"
    | "basicCompanyInfo.companyName"
    | "basicCompanyInfo.currentGrowthStage"
    | "productServiceDetails.currentProductStage";
  sortOrder?: "asc" | "desc";
  companyName?: string;
  growthStage?:
    | "All"
    | "Pre-seed"
    | "Seed"
    | "Series A"
    | "Series B+"
    | "Mature";
  productStage?:
    | "All"
    | "Idea/Concept"
    | "MVP"
    | "Beta"
    | "Launched"
    | "Scaling";
  dateFrom?: string;
  dateTo?: string;
}

class ProjectService implements Project {
  pathPrefix?: string;
  commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/projects";
    this.commonService = new CommonService();
  }

  // Create a new project
  createProject(projectData: ProjectData) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}`,
      data: projectData,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get all projects
  getProjects(params: GetProjectsParams) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}`,
      params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get a specific project
  getProject(projectId: string, userId: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}`,
      params: { userId },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Update a project
  updateProject(projectId: string, projectData: Partial<ProjectData>) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${projectId}`,
      data: projectData,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Delete a project
  deleteProject(projectId: string, userId: string) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: `${this.pathPrefix}/${projectId}`,
      params: { userId },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }
}

export default ProjectService;
